/**
 * Panning (`Space`, middle mouse, or the hand cursor) — a *viewport* gesture
 * that is available under every tool, so it is reduced before the active
 * tool sees the event (§9.2). Pan works in **screen** pixels: `tx`/`ty` are
 * the translation of the transform group, not percent coordinates.
 */
import type { ToolEvent, ToolMachine, ToolResult } from './types'
import { none, withState } from './types'

export const MIDDLE_BUTTON = 1

export interface PanContext {
  viewport: { k: number; tx: number; ty: number }
  /** True while `Space` is held or the pan tool is armed. */
  armed: boolean
}

/**
 * Returns a result when the event belongs to a pan, `null` when the active
 * tool should handle it instead.
 */
export function reducePan(machine: ToolMachine, ev: ToolEvent, ctx: PanContext): ToolResult | null {
  const st = machine.state

  if (ev.t === 'pointerdown' && (ev.button === MIDDLE_BUTTON || ctx.armed)) {
    return withState(machine.tool, { t: 'pan', start: { x: ev.screen.x, y: ev.screen.y }, origin: { tx: ctx.viewport.tx, ty: ctx.viewport.ty } })
  }

  if (st.t !== 'pan') return null

  if (ev.t === 'pointermove') {
    const tx = st.origin.tx + (ev.screen.x - st.start.x)
    const ty = st.origin.ty + (ev.screen.y - st.start.y)
    return withState(machine.tool, st, [{ k: 'panTo', tx, ty }])
  }

  if (ev.t === 'pointerup' || ev.t === 'pointercancel' || ev.t === 'cancel' || ev.t === 'spaceUp') {
    return { machine: { tool: machine.tool, state: { t: 'idle' } }, effects: [] }
  }

  return none(machine)
}

/** Zoom toward the cursor: `k' = clamp(k × 1.1^(−Δy/100), 0.1, 16)` (§9.2). */
export const MIN_ZOOM = 0.1
export const MAX_ZOOM = 16

export function zoomStep(k: number, deltaY: number): number {
  const next = k * Math.pow(1.1, -deltaY / 100)
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next))
}

/**
 * New translation so the image point currently under `(px, py)` (client
 * pixels relative to the canvas box) stays under it after the zoom.
 */
export function zoomAbout(viewport: { k: number; tx: number; ty: number }, nextK: number, px: number, py: number): { k: number; tx: number; ty: number } {
  const imgX = (px - viewport.tx) / viewport.k
  const imgY = (py - viewport.ty) / viewport.k
  return { k: nextK, tx: px - imgX * nextK, ty: py - imgY * nextK }
}
